import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import { CustomValueContainer } from '../../components/react_select/ReactSelect';
import ModalExtras from '../ventas/ModalExtras';
import TicketVuelta from './TicketVuelta';

// ─── Estilos React Select oscuro ────────────────────────────────────────────
const selectStyles = {
    control: (base, state) => ({
        ...base,
        background: '#1F1F1F',
        borderColor: state.isFocused ? '#F5C800' : 'rgba(245,200,0,0.4)',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(245,200,0,0.15)' : 'none',
        borderRadius: '10px',
        minHeight: '62px',
        '&:hover': { borderColor: '#F5C800' },
    }),
    menu: (base) => ({
        ...base,
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        zIndex: 99,
    }),
    option: (base, state) => ({
        ...base,
        background: state.isSelected ? 'rgba(245,200,0,0.15)' : state.isFocused ? '#222' : 'transparent',
        color: state.isSelected ? '#F5C800' : '#C0C0C0',
        cursor: 'pointer',
        fontSize: '1.1rem',
    }),
    singleValue: (base) => ({ ...base, color: '#F0F0F0', fontSize: '1.1rem' }),
    placeholder: (base) => ({ ...base, color: '#555', fontSize: '1.1rem' }),
    input: (base) => ({ ...base, color: '#F0F0F0', fontSize: '1.1rem' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#555' }),
    clearIndicator: (base) => ({ ...base, color: '#555' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// ─── Componente principal ────────────────────────────────────────────────────
const Vuelta = () => {
    const dispatch = useDispatch();

    // Catálogos
    const [productos, setProductos] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [empleadoLogueado, setEmpleadoLogueado] = useState(null);
    const [empleadosAsistieron, setEmpleadosAsistieron] = useState([]);
    const [eventoActivo, setEventoActivo] = useState(null);

    // Carrito
    const [carrito, setCarrito] = useState([]);

    // Selecciones
    const [vendedorData, setVendedorData] = useState(null);

    // Modal extras
    const [productoExtras, setProductoExtras] = useState(null);
    const [itemUidExtras, setItemUidExtras] = useState(null);

    // Status
    const [statusProcesar, setStatusProcesar] = useState(false);
    const [mostrarTicket, setMostrarTicket] = useState(false);
    const [ticketData, setTicketData] = useState(null);

    // Filtro productos
    const [busqueda, setBusqueda] = useState('');

    useEffect(() => {
        const init = async () => {
            const sesion = await db.sesion.toArray();
            setEmpleadoLogueado(sesion[0]);

            const emps = await db.empleados.toArray();
            const sucursalId = sesion[0].sucursal?._id || sesion[0].sucursal;
            const sucursalReg = await db.sucursales
                .filter(s => String(s._id || s.id) === String(sucursalId))
                .first();
            setEventoActivo(sucursalReg?.evento || null);

            const rawProds = await db.productos.toArray();
            const prods = rawProds
                .map(p => {
                    const entrada = p.precios?.find(pr =>
                        String(pr.sucursal?._id || pr.sucursal) === String(sucursalId)
                    );
                    return entrada ? { ...p, precio: entrada.precio } : p;
                })
                .sort((a, b) => {
                    if (a.tipo !== b.tipo) return a.tipo - b.tipo;
                    return a.nombre.localeCompare(b.nombre, 'es');
                });

            const todosAsistencias = await db.asistencias.toArray();
            const paseReciente = todosAsistencias.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0] || null;

            const idsAsistieron = new Set(
                (paseReciente?.empleados || [])
                    .filter(e => e.asistio === true)
                    .map(e => e.empleado)
            );

            setProductos(prods);
            setVendedores(
                emps
                    .filter(e => e.tipo === 4 && idsAsistieron.has(e._id))
                    .map(e => ({
                        value: e._id,
                        label: `${e.nombre} ${e.apellido_paterno || ''}`.trim(),
                        ...e,
                    }))
            );
            // Empleados que asistieron hoy, excluyendo tipo 4
            setEmpleadosAsistieron(
                emps.filter(e => e.tipo !== 4 && idsAsistieron.has(e._id))
            );
        };

        init();
    }, []);

    // ── Carrito ──────────────────────────────────────────────────────────────
    const handleAgregarProducto = (producto) => {
        setCarrito(prev => [...prev, {
            uid: Date.now() + Math.random(),
            producto_id: producto._id,
            producto_ref: producto,
            nombre: producto.nombre,
            precio_base: producto.precio,
            extras: [],
            precio_total: producto.precio,
            cantidad: 1,
        }]);
    };

    const handleCantidad = (uid, delta) => {
        setCarrito(prev => prev.map(item =>
            item.uid === uid
                ? { ...item, cantidad: Math.max(1, item.cantidad + delta) }
                : item
        ));
    };

    const handleEliminar = (uid) => {
        setCarrito(prev => prev.filter(item => item.uid !== uid));
    };

    const handleAbrirExtras = (item) => {
        setProductoExtras(item.producto_ref);
        setItemUidExtras(item.uid);
    };

    const handleGuardarExtras = (extrasSeleccionados) => {
        const precioExtras = extrasSeleccionados.reduce((acc, e) => acc + e.precio, 0);
        setCarrito(prev => prev.map(item =>
            item.uid === itemUidExtras
                ? { ...item, extras: extrasSeleccionados, precio_total: item.precio_base + precioExtras }
                : item
        ));
        setProductoExtras(null);
        setItemUidExtras(null);
    };

    const totalGeneral = carrito.reduce((acc, item) => acc + (item.precio_total * item.cantidad), 0);

    // ── Procesar vuelta ──────────────────────────────────────────────────────
    const handleProcesarVuelta = async () => {
        if (carrito.length === 0) return dispatch(setAlert('Agrega productos al carrito', 'danger'));
        if (!vendedorData) return dispatch(setAlert('Selecciona un vendedor', 'danger'));

        setStatusProcesar(true);

        try {
            const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const folio = letras[Math.floor(Math.random() * 26)] +
                          letras[Math.floor(Math.random() * 26)] +
                          Math.floor(Math.random() * 900 + 100);

            // Empleados involucrados: asistieron (tipo != 4 y != 5) + el vendedor
            const toEmpleadoObj = (emp) => {
                const tipo = emp.tipo ?? null;
                let totalComision = 0;
                carrito.forEach(item => {
                    const prod = item.producto_ref;
                    const comisionPersonal = prod.comisiones_empleado?.find(c => c.empleado === emp._id);
                    const comisionTipo     = prod.comisiones?.find(c => c.tipo === tipo);
                    const comision         = comisionPersonal ?? comisionTipo;
                    if (comision) totalComision += comision.cantidad * item.cantidad;
                });
                return { empleado: emp._id, tipo, total_comision: totalComision };
            };
            const empleadosVuelta = [
                ...empleadosAsistieron.map(toEmpleadoObj),
                toEmpleadoObj({ _id: vendedorData._id, tipo: vendedorData.tipo ?? null, producto_ref: null }),
            ];

            const vueltaLocal = {
                sucursal:    empleadoLogueado.sucursal?._id || empleadoLogueado.sucursal,
                responsable: empleadoLogueado._id,
                vendedor:    vendedorData._id,
                folio,
                evento:      eventoActivo?._id || null,
                fecha:       new Date(),
                total:       totalGeneral,
                status:      false,
                empleados:   empleadosVuelta,
            };

            const vueltaId = await db.vueltas.add(vueltaLocal);

            for (const item of carrito) {
                await db.vueltas_productos.add({
                    vuelta_local_id: vueltaId,
                    producto:        item.producto_id,
                    cantidad:        item.cantidad,
                    precio:          item.precio_total,
                    extras:          item.extras.map(e => ({ nombre: e.nombre, precio: e.precio })),
                });
            }

            setTicketData({
                folio,
                fecha:       new Date(),
                vendedor:    `${vendedorData.nombre || ''} ${vendedorData.apellido_paterno || ''}`.trim(),
                responsable: `${empleadoLogueado.nombre || ''} ${empleadoLogueado.apellido_paterno || ''}`.trim(),
                sucursal:    empleadoLogueado.sucursal?.nombre || '',
                carrito:     [...carrito],
                total:       totalGeneral,
            });
            setMostrarTicket(true);
        } catch {
            dispatch(setAlert('Error al registrar la venta ambulante', 'danger'));
        } finally {
            setStatusProcesar(false);
        }
    };

    const handleReset = () => {
        setCarrito([]);
        setVendedorData(null);
        setBusqueda('');
        setMostrarTicket(false);
        setTicketData(null);
    };

    const handleCancelar = () => {
        setCarrito([]);
        setVendedorData(null);
        setBusqueda('');
    };

    // ── Productos filtrados ──────────────────────────────────────────────────
    const productosFiltrados = busqueda
        ? productos.filter(p => p.nombre.toLowerCase().includes(busqueda.toLowerCase()))
        : productos;

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
                .prod-btn {
                    background: #1A1A1A;
                    border: 1px solid #2E2E2E;
                    border-radius: 12px;
                    padding: 1.2rem 1rem;
                    text-align: left;
                    width: 100%;
                    color: #F0F0F0;
                    cursor: pointer;
                    transition: border-color 0.15s, background 0.15s;
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                }
                .prod-btn:hover { border-color: #F5C800; background: rgba(245,200,0,0.04); }
                .prod-btn:active { background: rgba(245,200,0,0.08); }
                .carrito-fila {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.85rem;
                    padding: 1.1rem 1rem;
                    border-bottom: 1px solid #222;
                }
                .carrito-fila:last-child { border-bottom: none; }
                .btn-qty {
                    width: 46px; height: 46px;
                    background: #222;
                    border: 1px solid #333;
                    border-radius: 10px;
                    color: #F0F0F0;
                    font-size: 1.4rem;
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer;
                    flex-shrink: 0;
                    transition: background 0.15s;
                }
                .btn-qty:hover { background: #2E2E2E; }
            `}</style>

            <div style={styles.page}>
                <div style={styles.layout}>

                    {/* ── Panel izquierdo: Productos ── */}
                    <div style={styles.panelProductos}>
                        <div style={styles.sectionHeader}>
                            <span>Productos</span>
                            <span style={{ color: '#555', fontSize: '0.85rem' }}>
                                {productosFiltrados.length}
                            </span>
                        </div>

                        <input
                            type="text"
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            placeholder="Buscar producto..."
                            style={styles.buscador}
                        />

                        <div style={styles.gridProductos}>
                            {productosFiltrados.reduce((acc, prod, idx) => {
                                const anterior = productosFiltrados[idx - 1];
                                if (idx > 0 && prod.tipo !== anterior.tipo) {
                                    const ETIQUETAS = { 0: 'Alimentos', 1: 'Bebidas', 2: 'Extras' };
                                    acc.push(
                                        <div key={`sep-${prod.tipo}`} style={styles.separadorTipo}>
                                            <hr style={styles.separadorLinea} />
                                            <span style={styles.separadorLabel}>
                                                {ETIQUETAS[prod.tipo] ?? `Tipo ${prod.tipo}`}
                                            </span>
                                            <hr style={styles.separadorLinea} />
                                        </div>
                                    );
                                }
                                acc.push(
                                    <button
                                        key={prod._id || prod.id}
                                        className="prod-btn"
                                        onClick={() => handleAgregarProducto(prod)}
                                    >
                                        <span style={{ fontWeight: 600, fontSize: '1.85rem', lineHeight: 1.25 }}>
                                            {prod.nombre}
                                        </span>
                                        <span style={{ color: '#F5C800', fontWeight: 700, fontSize: '1.75rem' }}>
                                            ${(prod.precio || 0).toFixed(2)}
                                        </span>
                                    </button>
                                );
                                return acc;
                            }, [])}
                            {productosFiltrados.length === 0 && (
                                <p style={{ color: '#555', fontSize: '0.9rem', gridColumn: '1/-1' }}>
                                    Sin resultados
                                </p>
                            )}
                        </div>
                    </div>

                    {/* ── Panel derecho: Carrito + Datos ── */}
                    <div style={styles.panelCarrito}>

                        {/* Carrito */}
                        <div style={styles.sectionHeader}>
                            <span>Carrito</span>
                            {carrito.length > 0 && (
                                <span style={{ color: '#F5C800', fontWeight: 700 }}>
                                    {carrito.reduce((a, i) => a + i.cantidad, 0)} items
                                </span>
                            )}
                        </div>

                        <div style={styles.carritoBox}>
                            {carrito.length === 0 ? (
                                <div style={styles.carritoVacio}>
                                    <i className="fas fa-shopping-cart" style={{ fontSize: '2rem', color: '#333', marginBottom: '0.5rem' }}></i>
                                    <p style={{ color: '#555', margin: 0, fontSize: '0.9rem' }}>
                                        Agrega productos al carrito
                                    </p>
                                </div>
                            ) : (
                                carrito.map(item => (
                                    <div key={item.uid} className="carrito-fila">
                                        {/* Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ color: '#F0F0F0', fontWeight: 600, fontSize: '1.25rem' }}>
                                                {item.nombre}
                                            </div>
                                            {item.extras.length > 0 && (
                                                <div style={{ color: '#888', fontSize: '1rem', marginTop: '0.2rem' }}>
                                                    {item.extras.map(e => e.nombre).join(', ')}
                                                </div>
                                            )}
                                            <div style={{ color: '#F5C800', fontWeight: 700, fontSize: '1.15rem', marginTop: '0.25rem' }}>
                                                ${(item.precio_total * item.cantidad).toFixed(2)}
                                            </div>
                                        </div>

                                        {/* Controles */}
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem', flexShrink: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <button className="btn-qty" onClick={() => handleCantidad(item.uid, -1)}>−</button>
                                                <span style={{ color: '#F0F0F0', fontWeight: 700, minWidth: '28px', textAlign: 'center', fontSize: '1.2rem' }}>
                                                    {item.cantidad}
                                                </span>
                                                <button className="btn-qty" onClick={() => handleCantidad(item.uid, +1)}>+</button>
                                            </div>

                                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                {item.producto_ref?.extras?.length > 0 && (
                                                    <button
                                                        style={styles.btnExtras}
                                                        onClick={() => handleAbrirExtras(item)}
                                                    >
                                                        <i className="fas fa-plus-circle me-1"></i>
                                                        Extras
                                                    </button>
                                                )}
                                                <button style={styles.btnEliminar} onClick={() => handleEliminar(item.uid)}>
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Total */}
                        {carrito.length > 0 && (
                            <div style={styles.totalBox}>
                                <span style={{ color: '#888', fontSize: '0.95rem' }}>Total</span>
                                <span style={{ color: '#F5C800', fontWeight: 800, fontSize: '1.4rem' }}>
                                    ${totalGeneral.toFixed(2)}
                                </span>
                            </div>
                        )}

                        {/* Selects */}
                        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Select
                                options={vendedores}
                                value={vendedorData}
                                onChange={setVendedorData}
                                isClearable
                                isSearchable={false}
                                placeholder="Vendedor *"
                                styles={selectStyles}
                                components={{ ValueContainer: CustomValueContainer }}
                                menuPortalTarget={document.body}
                                menuPlacement="top"
                            />

                        </div>

                        {/* Botones acción */}
                        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem' }}>
                            <button
                                style={carrito.length === 0 && !vendedorData
                                    ? { ...styles.btnCancelar, opacity: 0.35, cursor: 'not-allowed' }
                                    : { ...styles.btnCancelar, flex: 1 }
                                }
                                onClick={handleCancelar}
                                disabled={carrito.length === 0 && !vendedorData}
                            >
                                <i className="fas fa-times me-2"></i>
                                Cancelar
                            </button>
                            <button
                                style={statusProcesar || carrito.length === 0
                                    ? { ...styles.btnProcesar, opacity: 0.5, cursor: 'not-allowed', flex: 1 }
                                    : { ...styles.btnProcesar, flex: 1 }
                                }
                                onClick={handleProcesarVuelta}
                                disabled={statusProcesar || carrito.length === 0}
                            >
                                {statusProcesar ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-check-circle me-2"></i>
                                        Procesar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal extras */}
            {productoExtras && (
                <ModalExtras
                    producto={productoExtras}
                    extrasSeleccionados={carrito.find(i => i.uid === itemUidExtras)?.extras || []}
                    onGuardar={handleGuardarExtras}
                    onCerrar={() => { setProductoExtras(null); setItemUidExtras(null); }}
                />
            )}

            {/* Ticket */}
            {mostrarTicket && ticketData && (
                <TicketVuelta data={ticketData} onCerrar={handleReset} />
            )}
        </>
    );
};

const styles = {
    page: {
        height: 'calc(95vh - 75px)',
        overflow: 'hidden',
    },
    layout: {
        display: 'grid',
        gridTemplateColumns: '1fr 500px',
        gap: '1.25rem',
        height: '100%',
    },
    panelProductos: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        overflow: 'hidden',
    },
    panelCarrito: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '1.1rem',
        overflow: 'hidden',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1rem',
        paddingBottom: '0.6rem',
        borderBottom: '1px solid #2E2E2E',
        marginBottom: '0.1rem',
        flexShrink: 0,
    },
    buscador: {
        background: '#1F1F1F',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.65rem 1rem',
        color: '#F0F0F0',
        fontSize: '0.95rem',
        width: '100%',
        outline: 'none',
        flexShrink: 0,
    },
    gridProductos: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
        gap: '0.75rem',
        overflowY: 'auto',
        flex: 1,
        paddingRight: '4px',
        alignContent: 'start',
    },
    carritoBox: {
        flex: 1,
        overflowY: 'auto',
        margin: '0.5rem -1.1rem 0',
        padding: '0 1.1rem',
    },
    carritoVacio: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2.5rem 0',
    },
    totalBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 0',
        borderTop: '1px solid #2E2E2E',
        marginTop: '0.5rem',
        flexShrink: 0,
    },
    btnExtras: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.5)',
        borderRadius: '8px',
        padding: '0.45rem 0.85rem',
        color: '#F5C800',
        fontSize: '1rem',
        cursor: 'pointer',
    },
    btnEliminar: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.5)',
        borderRadius: '8px',
        padding: '0.45rem 0.75rem',
        color: '#888',
        fontSize: '1rem',
        cursor: 'pointer',
    },
    separadorTipo: {
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        margin: '0.25rem 0',
    },
    separadorLinea: {
        flex: 1,
        borderColor: '#2E2E2E',
        margin: 0,
    },
    separadorLabel: {
        color: '#555',
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        whiteSpace: 'nowrap',
    },
    btnCancelar: {
        background: '#eb3508',
        color: '#ffffff',
        border: 'none',
        borderRadius: '12px',
        padding: '0.9rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        cursor: 'pointer',
        marginTop: '0.75rem',
        flexShrink: 0,
        transition: 'border-color 0.2s, color 0.2s',
    },
    btnProcesar: {
        background: '#F5C800',
        color: '#111',
        border: 'none',
        borderRadius: '12px',
        padding: '0.9rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        width: '100%',
        cursor: 'pointer',
        marginTop: '0.75rem',
        flexShrink: 0,
        transition: 'background 0.2s',
    },
};

export default Vuelta;
